
import React from 'react'
import PropTypes from 'prop-types'
import { Vector3 } from 'three'
import ObjectUpdater from './ObjectUpdater'
import RankingButton from '../../../Ranking/RankingButton'

import * as CamUtils from '../../../../Hex-map/camera-utils'

import { TextureLoader } from 'three'
import { MapView, Grid, mouseToWorld, vectorToQR } from '../../../../Hex-map'
// import { fillMap } from "../../../../Hex-map/util"

import * as Util from '../../../Util/util'

const ZOOM_LIMITS = {
  0: { x: [-6, 3], y: [-26.06, -31.06] },
  1: { x: [-12, 9.54], y: [-5, -24] },
  2: { x: [-17, 14.5], y: [8.25, -18.5] },
}

const MAX_ZOOM_IN = 1.5
const MAX_ZOOM_OUT = 40.0

class View extends React.Component {
  /****/

  constructor(props) {
    super(props)
    this.keyActions = {}
    this.state = {
      view: null,
      grid: null,
      options: null,
      zoomLevel: 0,
      hoverInfo: {
        building: {},
        position: {},
      },
    }
    
    // Bind methods to avoid creating new functions on each render
    this.centerOnQuarry = this.centerOnQuarry.bind(this)
  }

  // Optimize re-renders for this performance-critical component
  shouldComponentUpdate(nextProps, nextState) {
    // Always update if state changes (view, grid, zoomLevel, etc.)
    if (this.state !== nextState) {
      return true
    }

    // Critical props that affect 3D rendering
    const criticalProps = [
      'userMap', 'visitorMap', 'terrainAtlas', 'isAttacking', 
      'phaseInfo', 'listenerType'
    ]

    for (const prop of criticalProps) {
      if (this.props[prop] !== nextProps[prop]) {
        return true
      }
    }

    // Check if map buildings have changed (critical for 3D objects)
    if (this.props.userMap?.buildings !== nextProps.userMap?.buildings ||
        this.props.visitorMap?.buildings !== nextProps.visitorMap?.buildings) {
      return true
    }

    return false
  }

  componentDidMount() {
    const {
      terrainAtlas,
      seeds,
      zoomLevels,
      allAnimations,
      userMap,
      mapLoaded,
      mapLoadError,
      isTutorial,
      showError,
      hideError,
    } = this.props

    const textureLoader = new TextureLoader()
    const options = {
      terrainAtlas,
      terrainAtlasTexture: !isTutorial
        ? textureLoader.load(Util.asset('statics/textures/terrain.png'))
        : textureLoader.load(Util.asset('statics/textures/virtual_floor.png')),
      hillsNormalTexture: textureLoader.load(Util.asset('statics/textures/hills-normal.png')),
      terrainNormalTexture: textureLoader.load(Util.asset('statics/textures/terrain-normal.png')),
      undiscoveredTexture: textureLoader.load(Util.asset('statics/textures/paper.jpg')),
      transitionTexture: textureLoader.load(Util.asset('statics/textures/transitions.png')),
      planeTerrain: !isTutorial
        ? textureLoader.load(Util.asset('statics/textures/terrainTile.png'))
        : textureLoader.load(Util.asset('statics/textures/virtual_tile.png')),
      terrainDark: !isTutorial
        ? textureLoader.load(Util.asset('statics/textures/terrain2.png'))
        : textureLoader.load(Util.asset('statics/textures/virtual_mountains.png')),
      treeSpritesheetSubdivisions: 4,
      isTutorial: isTutorial,
    }

    const map_data = seeds[userMap.mapId]
    let mapView = null
    if (map_data) {
      mapView = new MapView('canvas', zoomLevels[this.state.zoomLevel])
      let grid = new Grid(map_data.data._width, map_data.data._height)

      grid.init(map_data.data.data)
      mapView.load(grid, options)

      mapView.animations = allAnimations
      let debugElement = document.getElementById('debug')
      mapView.controller.debugOutput = debugElement
      this.setState({ view: mapView, grid, options })
      mapLoaded()

      // let group = fillMap(map_data, 'models_draco/MARS_SPACESHIP_GAME.glb', mapView.modelFactory)

      // mapView.addCollection(group)
    } else {
      showError('CRITICAL_ERROR')
      mapLoadError()
      console.log('WEBGL: Load Error')
      setTimeout(() => {
        hideError()
      }, 250)
    }
  }

  componentWillUnmount() {
    const { clearSprites } = this.props
    const { view } = this.state
    if (view) {
      view.stop()
      view.destroyListeners()
    }
    clearSprites()
    this.setState({ view: null, grid: null, options: null })
  }

  componentDidUpdate() {
    const { view } = this.state

    if (view) {
      this.keyActions = {
        [Util.KEY_CODES.E]: {
          down: () => this.setBoundedZoom(view.getZoom() * 0.9),
        },
        [Util.KEY_CODES.Q]: {
          down: () => this.setBoundedZoom(view.getZoom() * 1.1),
        },
        [Util.KEY_CODES.W]: {
          down: () => view.rotateVector(new Vector3(1, 0, 0).normalize(), (1 * Math.PI) / 180),
        },
        [Util.KEY_CODES.S]: {
          down: () => view.rotateVector(new Vector3(1, 0, 0).normalize(), (-1 * Math.PI) / 180),
        },
        [Util.KEY_CODES.G]: {
          down: () => (view.mapMesh.showGrid = !view.mapMesh.showGrid),
        },
      }
    }
  }

  setBoundedZoom(zoom) {
    const { view } = this.state

    if (view) {
      let boundedZoom = Math.max(MAX_ZOOM_IN, Math.min(MAX_ZOOM_OUT, zoom))
      view.setZoom(boundedZoom)
    }
  }

  panManagement() {
    const { view, zoomLevel } = this.state
    let position = view._camera.position

    //reviews x
    let zoomXRange = ZOOM_LIMITS[zoomLevel].x

    let copX = position.x
    //exits left
    if (copX < zoomXRange[0]) {
      position.x = zoomXRange[0]
    }
    //exits  right
    if (copX > zoomXRange[1]) {
      position.x = zoomXRange[1]
    }
    //Reviews y
    let zoomYRange = ZOOM_LIMITS[zoomLevel].y
    let copY = position.y
    // exits up
    if (copY > zoomYRange[0]) {
      position.y = zoomYRange[0]
    }
    //exits down
    if (copY < zoomYRange[1]) {
      position.y = zoomYRange[1]
    }
    //Center the camera to new position
    if (copX !== position.x || copY !== position.y) {
      CamUtils.screenCenterToWorld(view._camera)
    }
  }

  handleKeyDown(e) {
    const actions = this.keyActions[e.keyCode]

    if (actions && 'down' in actions) {
      actions['down']()
    }
  }

  handleKeyUp(e) {
    const actions = this.keyActions[e.keyCode]

    if (actions && 'up' in actions) {
      actions['up']()
    }
  }

  handleWheel(e) {
    const { zoomLevels } = this.props
    const { view, zoomLevel } = this.state

    const zoomIncrement = e.deltaY < 0 ? 1 : e.deltaY > 0 ? -1 : 0
    const canZoom = zoomLevel + zoomIncrement < zoomLevels.length && zoomLevel + zoomIncrement >= 0

    if (view && canZoom) {
      let zoomAmount = zoomLevels[zoomLevel + zoomIncrement]
      view.setZoom(zoomAmount)
      this.setState({ zoomLevel: zoomLevel + zoomIncrement })
    }
  }

  centerOnQuarry() {
    const { view } = this.state
    const { userMap, visitorMap } = this.props

    let usingMap = visitorMap.mapId ? visitorMap : userMap

    let quarry = Object.values(usingMap.buildings).find((building) => building.type == 'mine')
    if (!quarry) {
      return null
    }
    view.controller.panCameraTo({ q: quarry.q, r: quarry.r }, 600)
  }

  setCurrentZoom(zoomLevel) {
    const { zoomLevels, addHowlSound } = this.props
    const { view } = this.state

    const canZoom = zoomLevel < zoomLevels.length && zoomLevel >= 0

    if (view && canZoom) {
      view.setZoom(zoomLevels[zoomLevel])
      this.setState({ zoomLevel: zoomLevel })
    }
    addHowlSound('button_click_icon')
  }

  handleClick() {
    const { selectCurrentTile, addPopup, setDisplayType, addHowlSound, getBattle } = this.props

    const { view } = this.state
    let selectedTile = view.selectedTile
    if (selectedTile && (!view.controller.oldDrag || view.controller.oldDrag.length() < 0.1)) {
      getBattle()
      selectCurrentTile(selectedTile)
      addPopup('tile_popup')
      addHowlSound('select_tile_on_map')
    }
    setDisplayType(null)
    this.panManagement()
  }

  handleMouseMove(e) {
    const { view, hoverInfo } = this.state

    const { userMap, visitorMap } = this.props

    if (view) {
      let mouseVector = mouseToWorld(e, view.getCamera())
      if (!mouseVector) return // Skip if ray doesn't intersect Z plane

      let mousePos = {
        x: e.clientX,
        y: e.clientY,
      }
      let mouseQR = vectorToQR(mouseVector)
      let usingMap = visitorMap.mapId ? visitorMap : userMap
      let buildings = usingMap.buildings
      let building = buildings[mouseQR.q + '_' + mouseQR.r]

      if (
        building &&
        building.built &&
        building.type != 'spaceship' &&
        building.type != 'mine' &&
        building.type != 'debris'
      ) {
        this.setState({
          hoverInfo: {
            building,
            position: mousePos,
          },
        })
      } else {
        if (Object.keys(hoverInfo).length != 0) {
          this.setState({
            hoverInfo: {
              building: {},
              position: {},
            },
          })
        }
      }
    }
  }

  /**
   *  Render component
   *  @return {React.Component} The component
   */
  render() {
    const { view, grid, options, zoomLevel } = this.state

    const {
      phaseInfo,
      addHowlSound,
      userMap,
      hideRanking,
      hideZoom,
      isAttacking,
      visitorMap,
      visitorInfo,
      listenerType,
    } = this.props

    const events = {
      onKeyDown: (e) => this.handleKeyDown(e),
      onKeyUp: (e) => this.handleKeyUp(e),
      onClick: (e) => this.handleClick(e),
      onTouchEnd: (e) => this.handleClick(e),
    }
    const ZoomComponent = ({ zoomLevel, addHowlSound }) => (
      <div className={zoomLevel == 0 ? 'zoom active' : 'zoom'}>
        <div
          className="zoom--nivel"
          onClick={() => {
            this.setCurrentZoom(1)
          }}
        >
          <span className="icon-zoom-max"> </span>
        </div>
        <div
          className="zoom--nivel"
          onClick={() => {
            this.setCurrentZoom(0)
          }}
        >
          <span className="icon-zoom-min"> </span>
        </div>
        <div
          className="zoom--centered"
          onClick={() => {
            this.centerOnQuarry()
            addHowlSound('button_click_icon')
          }}
        >
          <span className="icon-id"> </span>
        </div>
      </div>
    )
    return (
      <div className={`'map--view' ${isAttacking ? 'frame' : null}`}>
        {!hideZoom && <ZoomComponent zoomLevel={zoomLevel} addHowlSound={addHowlSound} />}
        {!hideRanking && <RankingButton />}
        <ObjectUpdater
          view={view}
          currentGrid={grid}
          currentOptions={options}
          phaseInfo={phaseInfo}
          userMap={userMap}
          visitorMap={visitorMap}
          visitorInfo={visitorInfo}
          listenerType={listenerType}
        />

        <canvas id="main-canvas" className="canvas--view" {...events} tabIndex="0"></canvas>
      </div>
    )
  }
}

View.propTypes = {
  selectCurrentTile: PropTypes.func,
  setDisplayType: PropTypes.func,
  addPopup: PropTypes.func,
  getBattle: PropTypes.func,
  clearSprites: PropTypes.func,
  view: PropTypes.object,
  visitorMap: PropTypes.object,
  visitorInfo: PropTypes.object,
  userMap: PropTypes.object,
  terrainAtlas: PropTypes.object,
  isTutorial: PropTypes.bool,
  isAttacking: PropTypes.bool,
  listenerType: PropTypes.object,
  seeds: PropTypes.object,
  zoomLevels: PropTypes.array,
  allAnimations: PropTypes.object,
  mapLoaded: PropTypes.func,
  mapLoadError: PropTypes.func,
  showError: PropTypes.func,
  hideError: PropTypes.func,
  addHowlSound: PropTypes.func,
  phaseInfo: PropTypes.object,
  hideRanking: PropTypes.bool,
  hideZoom: PropTypes.bool,
}

export default View
